/**
 * GMA-1625 (CompassCare Web 10/14 — INTEL) — caregiver intelligence page.
 *
 * Mirrors the hi-fi mockup (CompassCare INTEL.dc.html): an ACTIVE WATCHES rail
 * sorted by impact (severity chip, one-line summary, impacted count, detection
 * lead, source tag) plus SAVED WATCH PROFILES toggles, and a watch-detail
 * panel — impact KPIs (clients impacted / detection lead / window), detection
 * mechanism chips + narrative, "why this matters" behavior & wellness
 * triggers, NOW / THIS SHIFT / TONIGHT recommended caregiver actions, NORA
 * operational posture, and Generate Intel SITRep / Notify actions. When the
 * router provides onGenerateSitrep, the SITRep button hands the watch to the
 * SITRep board (GMA-1624, /web/intel/sitrep); without it, the button keeps
 * its honest queued-note stub (the SITRep prose itself is Nora backend work).
 *
 * Impact is CONCRETE (AC): zone watches resolve to clients geographically via
 * intelModel.clientsInWatchZone; cohort watches (e.g. the NORA companion
 * watch) carry explicit clientIds. Fail-safe: no watches renders a calm empty
 * state, unknown severities degrade to the benign token, missing sections are
 * simply omitted — never a throw.
 */
import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { theme, severityToken } from "../../styles/designTokens";
import { clientsInWatchZone, watchTypeFor } from "./intelModel";

const SEVERITY_ORDER = { critical: 0, atRisk: 1, caution: 2, normal: 3 };

/** Concrete impacted clients for a watch: explicit cohort or geographic zone. */
export function resolveWatchImpact(watch, clients) {
  const list = Array.isArray(clients) ? clients.filter(Boolean) : [];
  if (watch && Array.isArray(watch.clientIds)) {
    const ids = new Set(watch.clientIds);
    return list.filter((c) => ids.has(c.id));
  }
  if (watch && watch.zone) {
    return clientsInWatchZone(
      { center: watch.zone.center, radiusKm: watch.zone.radiusKm },
      list,
    );
  }
  return [];
}

/** Watches decorated with their impacted clients, sorted by impact. */
export function rankWatches(watches, clients) {
  const list = Array.isArray(watches) ? watches.filter(Boolean) : [];
  return list
    .map((w) => ({ ...w, impacted: resolveWatchImpact(w, clients) }))
    .sort((a, b) => {
      const sev =
        (SEVERITY_ORDER[a.severity] ?? 9) - (SEVERITY_ORDER[b.severity] ?? 9);
      if (sev !== 0) return sev;
      return b.impacted.length - a.impacted.length;
    });
}

function Chip({ children, color, background }) {
  return (
    <span
      style={{
        display: "inline-block",
        padding: "2px 8px",
        borderRadius: 6,
        fontSize: 10.5,
        fontWeight: 700,
        letterSpacing: 0.6,
        color: color || theme.textMuted,
        background: background || theme.surface,
        border: `1px solid ${theme.border}`,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </span>
  );
}

Chip.propTypes = {
  children: PropTypes.node,
  color: PropTypes.string,
  background: PropTypes.string,
};

function SeverityChip({ watch }) {
  const token = severityToken(watch.severity);
  return (
    <Chip color={token.color} background={`${token.color}1f`}>
      {watch.severityChip || token.label.toUpperCase()}
    </Chip>
  );
}

SeverityChip.propTypes = { watch: PropTypes.object.isRequired };

function WatchCard({ watch, selected, onSelect }) {
  const token = severityToken(watch.severity);
  return (
    <button
      type="button"
      data-testid="watch-card"
      data-watch-id={watch.id}
      onClick={() => onSelect(watch)}
      aria-current={selected ? "true" : undefined}
      style={{
        display: "grid",
        gap: 6,
        textAlign: "left",
        padding: "11px 12px",
        borderRadius: 10,
        cursor: "pointer",
        background: selected ? theme.surface : "transparent",
        border: `1px solid ${selected ? token.color : theme.border}`,
        color: theme.textPrimary,
        font: `13px ${theme.fontUI}`,
      }}
    >
      <span style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        <strong style={{ fontSize: 13.5 }}>{watch.title}</strong>
        <SeverityChip watch={watch} />
      </span>
      <span style={{ color: theme.textMuted, fontSize: 12.5, lineHeight: 1.35 }}>{watch.summary}</span>
      <span style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 11.5, flexWrap: "wrap" }}>
        <span style={{ color: token.color, fontWeight: 700 }}>
          {watch.impacted.length} impacted
        </span>
        <span style={{ color: "#2ECC8F", fontFamily: theme.fontMono }}>{watch.lead}</span>
        <span style={{ color: "#8B7CF6", marginLeft: "auto" }}>{watch.sourceTag}</span>
      </span>
    </button>
  );
}

WatchCard.propTypes = {
  watch: PropTypes.object.isRequired,
  selected: PropTypes.bool,
  onSelect: PropTypes.func.isRequired,
};

function SectionLabel({ children }) {
  return (
    <span
      style={{
        fontSize: 10.5,
        fontWeight: 700,
        letterSpacing: 1,
        color: theme.textMuted,
        textTransform: "uppercase",
      }}
    >
      {children}
    </span>
  );
}

SectionLabel.propTypes = { children: PropTypes.node };

function Kpi({ label, value, accent }) {
  return (
    <div
      style={{
        border: `1px solid ${theme.border}`,
        borderRadius: 10,
        padding: "10px 12px",
        display: "grid",
        gap: 3,
        background: theme.surface,
      }}
    >
      <SectionLabel>{label}</SectionLabel>
      <strong style={{ fontSize: 17, color: accent || theme.textPrimary, fontFamily: theme.fontMono }}>
        {value}
      </strong>
    </div>
  );
}

Kpi.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node,
  accent: PropTypes.string,
};

const ACTION_TINT = {
  NOW: "#F0524E",
  "THIS SHIFT": "#F5883D",
  TONIGHT: "#F5C542",
};

function WatchDetail({ watch, onGenerateSitrep }) {
  const [sitrepQueued, setSitrepQueued] = useState(false);
  const [notified, setNotified] = useState(false);
  if (!watch) return null;
  const token = severityToken(watch.severity);
  // #413: own-property lookup — WATCH_TYPES["constructor"] is truthy and
  // would skip the not-found branch, then throw on .triggers.map.
  const type = watchTypeFor(watch.typeKey);
  const triggers =
    Array.isArray(watch.triggers) && watch.triggers.length
      ? watch.triggers
      : (type ? type.triggers : []).map((t) => ({ term: t, text: "" }));
  return (
    <div data-testid="watch-detail" style={{ display: "grid", gap: 12 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        <div style={{ display: "grid", gap: 2 }}>
          <strong style={{ fontSize: 16 }}>{watch.title}</strong>
          {watch.subtitle ? (
            <span style={{ color: theme.textMuted, fontSize: 12 }}>{watch.subtitle}</span>
          ) : null}
        </div>
        <SeverityChip watch={watch} />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0,1fr))", gap: 8 }}>
        <Kpi label="Clients impacted" value={watch.impacted.length} accent={token.color} />
        <Kpi label="Detection lead" value={watch.lead || "—"} accent="#2ECC8F" />
        <Kpi label="Window" value={watch.window || "—"} />
      </div>

      {watch.detection ? (
        <div style={{ border: `1px solid ${theme.border}`, borderRadius: 10, padding: 12, display: "grid", gap: 8 }}>
          <SectionLabel>Detection mechanism</SectionLabel>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {(watch.detection.chips || []).map((c) => (
              <Chip key={c}>{c}</Chip>
            ))}
          </div>
          {watch.detection.text ? (
            <span style={{ color: theme.textMuted, fontSize: 12.5, lineHeight: 1.45 }}>
              {watch.detection.text}
            </span>
          ) : null}
        </div>
      ) : null}

      {triggers.length ? (
        <div
          style={{
            border: `1px solid #8B7CF640`,
            background: "#8B7CF612",
            borderRadius: 10,
            padding: 12,
            display: "grid",
            gap: 7,
          }}
        >
          <SectionLabel>Why this matters — behavior & wellness triggers</SectionLabel>
          {triggers.map((t) => (
            <span key={t.term} style={{ fontSize: 12.5, lineHeight: 1.45 }}>
              <strong>{t.term}</strong>
              {t.text ? <span style={{ color: theme.textMuted }}> → {t.text}</span> : null}
            </span>
          ))}
        </div>
      ) : null}

      {watch.actions && watch.actions.length ? (
        <div style={{ display: "grid", gap: 6 }}>
          <SectionLabel>Recommended caregiver actions</SectionLabel>
          {watch.actions.map((a) => (
            <div key={`${a.when}-${a.text.slice(0, 16)}`} style={{ display: "flex", gap: 9, alignItems: "flex-start" }}>
              <span
                style={{
                  flex: "none",
                  minWidth: 74,
                  textAlign: "center",
                  padding: "3px 7px",
                  borderRadius: 6,
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: 0.7,
                  color: ACTION_TINT[a.when] || theme.textMuted,
                  background: `${ACTION_TINT[a.when] || theme.textMuted}1a`,
                }}
              >
                {a.when}
              </span>
              <span style={{ fontSize: 12.5, color: theme.textPrimary, lineHeight: 1.45 }}>{a.text}</span>
            </div>
          ))}
        </div>
      ) : null}

      {watch.impacted.length ? (
        <div style={{ border: `1px solid ${theme.border}`, borderRadius: 10, padding: 12, display: "grid", gap: 6 }}>
          <SectionLabel>Impacted clients ({watch.impacted.length})</SectionLabel>
          <div data-testid="watch-impacted" style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {watch.impacted.map((c) => (
              <Chip key={c.id} color={severityToken(c.status).color}>
                {c.name}
              </Chip>
            ))}
          </div>
        </div>
      ) : null}

      {watch.posture ? (
        <div style={{ border: `1px solid ${theme.border}`, borderRadius: 10, padding: 12, display: "grid", gap: 6 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
            <SectionLabel>NORA operational posture</SectionLabel>
            {watch.posture.confidence ? (
              <Chip color="#2ECC8F" background="#2ECC8F14">
                {watch.posture.confidence}
              </Chip>
            ) : null}
          </div>
          {watch.posture.level ? (
            <span style={{ color: "#8B7CF6", fontSize: 12, fontWeight: 700 }}>{watch.posture.level}</span>
          ) : null}
          {watch.posture.text ? (
            <span style={{ color: theme.textMuted, fontSize: 12.5, lineHeight: 1.45 }}>{watch.posture.text}</span>
          ) : null}
        </div>
      ) : null}

      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <button
          type="button"
          data-testid="watch-sitrep-btn"
          onClick={() =>
            // GMA-1624: when the router wires the SITRep board, hand the watch
            // off to it; otherwise keep the honest queued-note stub.
            onGenerateSitrep ? onGenerateSitrep(watch) : setSitrepQueued(true)
          }
          style={{
            flex: 1,
            padding: "10px 12px",
            borderRadius: 9,
            border: "none",
            cursor: "pointer",
            background: "#8B7CF6",
            color: "#0A0D13",
            font: `700 13px ${theme.fontUI}`,
          }}
        >
          Generate Intel SITRep
        </button>
        <button
          type="button"
          data-testid="watch-notify-btn"
          onClick={() => setNotified(true)}
          style={{
            padding: "10px 16px",
            borderRadius: 9,
            border: `1px solid #2ECC8F`,
            cursor: "pointer",
            background: "transparent",
            color: "#2ECC8F",
            font: `700 13px ${theme.fontUI}`,
          }}
        >
          Notify
        </button>
      </div>
      {sitrepQueued ? (
        <span data-testid="watch-sitrep-note" style={{ color: theme.textMuted, fontSize: 12 }}>
          SITRep request queued for Nora — generation wires to the Nora backend (GMA-1624).
        </span>
      ) : null}
      {notified ? (
        <span data-testid="watch-notify-note" style={{ color: theme.textMuted, fontSize: 12 }}>
          Caregiver notification drafts open in Messages once live alert wiring lands.
        </span>
      ) : null}
    </div>
  );
}

WatchDetail.propTypes = {
  watch: PropTypes.object,
  onGenerateSitrep: PropTypes.func,
};

export default function IntelPage({
  watches = [],
  profiles = [],
  clients = [],
  onGenerateSitrep,
}) {
  const ranked = useMemo(() => rankWatches(watches, clients), [watches, clients]);
  const [selectedId, setSelectedId] = useState(null);
  const [profileState, setProfileState] = useState(() =>
    Object.fromEntries((profiles || []).filter(Boolean).map((p) => [p.id, !!p.on])),
  );
  const selected =
    ranked.find((w) => w.id === selectedId) || ranked[0] || null;

  return (
    <div
      data-testid="intel-page"
      style={{
        fontFamily: theme.fontUI,
        color: theme.textPrimary,
        display: "grid",
        gridTemplateColumns: "minmax(280px, 380px) minmax(0, 1fr)",
        gap: 12,
        alignItems: "start",
      }}
    >
      <div style={{ display: "grid", gap: 10 }}>
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
          <SectionLabel>Active watches</SectionLabel>
          <span style={{ color: theme.textMuted, fontSize: 11 }}>sorted by impact</span>
        </div>
        <div data-testid="watch-rail" style={{ display: "grid", gap: 8 }}>
          {ranked.length === 0 ? (
            <span style={{ color: theme.textMuted, fontSize: 12.5 }}>
              No active watches. NORA is monitoring feeds and will raise a watch
              when conditions warrant.
            </span>
          ) : (
            ranked.map((w) => (
              <WatchCard
                key={w.id}
                watch={w}
                selected={selected ? selected.id === w.id : false}
                onSelect={(watch) => setSelectedId(watch.id)}
              />
            ))
          )}
        </div>

        {profiles && profiles.length ? (
          <div style={{ border: `1px solid ${theme.border}`, borderRadius: 10, padding: 12, display: "grid", gap: 8 }}>
            <SectionLabel>Saved watch profiles</SectionLabel>
            {profiles.map((p) => (
              <div key={p.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                <span style={{ fontSize: 12.5 }}>{p.label}</span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={!!profileState[p.id]}
                  aria-label={`${p.label} profile`}
                  data-testid="watch-profile-toggle"
                  onClick={() =>
                    setProfileState((s) => ({ ...s, [p.id]: !s[p.id] }))
                  }
                  style={{
                    border: `1px solid ${theme.border}`,
                    borderRadius: 12,
                    padding: "2px 10px",
                    cursor: "pointer",
                    background: profileState[p.id] ? "#2ECC8F22" : "transparent",
                    color: profileState[p.id] ? "#2ECC8F" : theme.textMuted,
                    font: `700 11px ${theme.fontUI}`,
                  }}
                >
                  {profileState[p.id] ? "On" : "Off"}
                </button>
              </div>
            ))}
          </div>
        ) : null}
      </div>

      <div
        style={{
          border: `1px solid ${theme.border}`,
          borderRadius: 10,
          padding: 14,
          background: theme.bg,
          minHeight: 320,
        }}
      >
        {selected ? (
          <WatchDetail watch={selected} onGenerateSitrep={onGenerateSitrep} />
        ) : (
          <span style={{ color: theme.textMuted, fontSize: 12.5 }}>
            Select a watch to see its detail.
          </span>
        )}
      </div>
    </div>
  );
}

IntelPage.propTypes = {
  watches: PropTypes.arrayOf(PropTypes.object),
  profiles: PropTypes.arrayOf(PropTypes.object),
  clients: PropTypes.arrayOf(PropTypes.object),
  /** Called with the selected watch — the router opens the SITRep board
      (GMA-1624). Without it the button keeps its honest queued-note stub. */
  onGenerateSitrep: PropTypes.func,
};
